import { useId, useRef, useState, type KeyboardEvent } from "react";
import type { Subtask, Worker, WorkerTaskEfficiency } from "../types";
import { efficiencyKey } from "../efficiencyPresentation";

export function EfficiencyMatrix({ workers, tasks, values, edits, busy, mode = "rate", onEdit }: {
  workers: Worker[]; tasks: Subtask[]; values: Map<string, WorkerTaskEfficiency>; edits: Map<string, string>; busy: boolean;
  mode?: "rate" | "smv";
  onEdit: (workerId: string, taskId: string, value: string) => void;
}) {
  const [activeKey, setActiveKey] = useState("");
  const cells = useRef(new Map<string, HTMLInputElement>());
  const helpId = useId();
  const keys = new Set(workers.flatMap(worker => tasks.map(task => efficiencyKey(worker.id, task.id))));
  const stop = keys.has(activeKey) ? activeKey : workers[0] && tasks[0] ? efficiencyKey(workers[0].id, tasks[0].id) : "";
  const move = (event: KeyboardEvent<HTMLInputElement>, row: number, column: number) => {
    const input = event.currentTarget;
    const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
    const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
    const key =
      event.key === "ArrowUp" ? "up" : event.key === "ArrowDown" ? "down" :
      event.key === "ArrowLeft" && atStart ? "left" : event.key === "ArrowRight" && atEnd ? "right" : null;
    if (!key || busy) return;
    event.preventDefault();
    if (key === "up") row--; if (key === "down") row++;
    if (key === "left") column--; if (key === "right") column++;
    row = Math.max(0, Math.min(workers.length - 1, row)); column = Math.max(0, Math.min(tasks.length - 1, column));
    const target = cells.current.get(efficiencyKey(workers[row].id, tasks[column].id));
    target?.focus({ preventScroll: true });
    target?.select();
  };
  return <div className="efficiency-matrix-scroll" role="region" aria-label="작업자별 단위 작업 효율" tabIndex={0} data-testid="efficiency-matrix">
    <p className="efficiency-keyboard-help" id={helpId}>방향키로 값을 이동하고 입력 후 Enter나 다른 칸으로 이동하면 반영됩니다. 아래 저장 버튼을 눌러야 실제로 저장됩니다.</p>
    <table className="efficiency-matrix" aria-describedby={helpId}>
      <thead><tr><th scope="col">작업자 <span>{mode === "smv" ? "분/개 · SMV" : "개/시간"}</span></th>{tasks.map((task, column) => <th scope="col" key={task.id}><span className="efficiency-task-number">{column + 1}{task.roundNumber ? ` · ${task.roundNumber}차` : ""}</span><span className="efficiency-task-title" title={task.name}>{task.name}</span></th>)}</tr></thead>
      <tbody>{workers.map((worker, row) => <tr key={worker.id} data-testid="efficiency-matrix-worker"><th scope="row"><strong title={worker.displayedName}>{worker.displayedName}</strong>{worker.kind === "robot" && <small>로봇</small>}</th>
        {tasks.map((task, column) => {
          const key = efficiencyKey(worker.id, task.id), value = values.get(key);
          const edited = edits.get(key);
          const smv = value?.smvMinutes ?? value?.estimatedSmvMinutes ?? (value?.unitsPerHour ? Math.round(60 / value.unitsPerHour * 10_000) / 10_000 : null);
          const draft = edited ?? (mode === "smv" ? smv == null ? "" : String(smv) : value ? String(value.unitsPerHour) : "");
          const dotSource = edited !== undefined ? "manual" : value?.source ?? "unset";
          return <td key={task.id} className={dotSource} data-testid="efficiency-matrix-value">
            <input ref={element => { if (element) cells.current.set(key, element); else cells.current.delete(key); }}
              type="number" inputMode="decimal" min={mode === "smv" ? 0.001 : 0} max={mode === "smv" ? 600 : 1_000_000} step="any" className="efficiency-cell"
              disabled={busy} tabIndex={stop === key ? 0 : -1} data-worker-id={worker.id} data-task-id={task.id}
              aria-label={`${worker.displayedName} · ${task.name} · ${mode === "smv" ? "SMV 분/개" : "시간당 생산량"} · ${dotSource === "unset" ? "미설정" : dotSource === "manual" ? "수동 수정" : "초기 계산"}`}
              value={draft} placeholder="—"
              onFocus={event => { setActiveKey(key); event.currentTarget.select(); }}
              onChange={event => onEdit(worker.id, task.id, event.target.value)}
              onKeyDown={event => move(event, row, column)} />
            <i className={dotSource} aria-hidden="true" />
          </td>;
        })}
      </tr>)}</tbody>
    </table>
  </div>;
}
